/**
 * @module app.shared
 */

import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { autoinject, signalBindings } from "aurelia-framework";
import { signalNames } from 'app/shared/lib/signals';
import { DateTimeValue } from 'app/shared/lib/date-time';
import { Constructor } from "../lib/types";

/**
 * Base class for creating services that fire events.
 * @abstract @class
 * @param EventName Type of possible event names.
 * @param EventType Type of the event passed to handlers.
 */
@autoinject()
export abstract class EventServiceBase<EventName extends string, EventType> {

    protected static events: Record<string, Constructor>;

    protected subscriptions: Record<EventName, Map<(event: EventType) => unknown, Subscription>>;

    public addEventListener(event: EventName, handler: (event: EventType) => unknown) {
        const Event = (<any>this.constructor).events[event] as Constructor | undefined;
        if(Event) {
            const subscription = this.events.subscribe(Event, handler);
            this.subscriptions[event].set(handler, subscription);
        }
    }

    public removeEventListener(event: EventName, handler: (event: EventType) => unknown) {
        const subscription = this.subscriptions[event].get(handler);
        if(subscription) {
            subscription.dispose();
        }
    }

    constructor(private events: EventAggregator) {
        this.subscriptions = {} as Record<EventName, Map<(event: EventType) => unknown, Subscription>>;
        for(let event in(<any>this.constructor).events) {
            this.subscriptions[event as EventName] = new Map();
        }
        this.init(events);
    }

    protected abstract init(events: EventAggregator): any;
}

export interface IFrameEvent { }
export type FrameHandler = (event: IFrameEvent) => any;

/**
 * Fires a FrameEvent for each requestAnimationFrame callback.
 * @class
 */
export class AnimationFrameService extends EventServiceBase<keyof typeof AnimationFrameService.events, IFrameEvent> {

    protected static events = {
        frame: class FrameEvent implements IFrameEvent { }
    };

    protected init(events: EventAggregator) {
        const FrameEvent = AnimationFrameService.events['frame'];
        (function recursed() {
            requestAnimationFrame(recursed);
            events.publish(new FrameEvent());
        })();
    }
}

/** Represents information about a clock tick */
export interface ITickEvent {
    /** Current clock timestamp */
    readonly time: number;
    /** Number of milliseconds since the previous tick */
    readonly elapsed: number;
}

/** Handler for a TickEvent */
export type TickHandler = (event: ITickEvent) => any;

/**
 * Fires a TickEvent every second.
 * @class
 */
export class TickService extends EventServiceBase<keyof typeof TickService.events, ITickEvent> {

    protected static events = {
        tick: class TickEvent implements ITickEvent {
            constructor(public readonly time: number, public readonly elapsed: number) { }
        }
    };

    /**
     * A bindable value of the current time.
     */
    public now: string = DateTimeValue.now().value!;

    protected init(events: EventAggregator) {
        const TickEvent = TickService.events['tick'];
        let previous: number = Date.now();
        setInterval(() => {
            const time = Date.now();
            events.publish(new TickEvent(time, time - previous));
            previous = time;
        }, 1000);
        this.addEventListener('tick', ({ time }) => this.now = new DateTimeValue(new Date(time).toISOString()).value!);
        this.addEventListener('tick', () => signalBindings(signalNames.clockTickSecond));
    }

}
